import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ClipboardList, 
  Trash2, 
  Search,
  Filter,
  AlertTriangle,
  Calendar,
  User,
  Mail,
  Building,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '../utils/logger';

const AssessmentManagement = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user?.rol !== 'company_admin') {
      navigate('/dashboard');
      return;
    }
    loadAssessments();
  }, [user, navigate]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/companies/assessments/list');
      if (response.data.success) {
        setAssessments(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading assessments:', error);
      toast.error(
        language === 'es' 
          ? 'Error al cargar evaluaciones' 
          : 'Error loading assessments'
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completado':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            {language === 'es' ? 'Completada' : 'Completed'}
          </Badge>
        );
      case 'en_progreso':
      case 'iniciado':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            {language === 'es' ? 'En Progreso' : 'In Progress'}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200">
            <XCircle className="h-3 w-3 mr-1" />
            {status}
          </Badge>
        );
    }
  };

  const getRoleBadge = (rol) => {
    return (
      <Badge variant={rol === 'employee' ? "default" : "secondary"}>
        {rol === 'employee' 
          ? (language === 'es' ? 'Empleado' : 'Employee')
          : (language === 'es' ? 'Candidato' : 'Candidate')
        }
      </Badge>
    );
  };

  const handleDeleteClick = (assessment) => {
    setDeleteConfirm(assessment);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`/companies/assessments/${deleteConfirm.id}`);
      if (response.data.success) {
        toast.success(
          language === 'es' 
            ? `Evaluación eliminada para ${deleteConfirm.usuario_nombre}` 
            : `Assessment deleted for ${deleteConfirm.usuario_nombre}`
        );
        loadAssessments();
      }
    } catch (error) {
      logger.error('Error deleting assessment:', error);
      toast.error(
        language === 'es' 
          ? 'Error al eliminar evaluación' 
          : 'Error deleting assessment'
      );
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const filteredAssessments = assessments.filter(assessment => {
    const matchesSearch = 
      assessment.usuario_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.usuario_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.departamento?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'completed' && assessment.status === 'completado') ||
      (filterStatus === 'in_progress' && assessment.status !== 'completado');
    
    const matchesRole = filterRole === 'all' || assessment.usuario_rol === filterRole;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  // Group assessments by user
  const groupedByUser = filteredAssessments.reduce((acc, assessment) => {
    const key = assessment.usuario_id;
    if (!acc[key]) {
      acc[key] = {
        usuario_id: assessment.usuario_id,
        usuario_nombre: assessment.usuario_nombre,
        usuario_email: assessment.usuario_email,
        usuario_rol: assessment.usuario_rol,
        departamento: assessment.departamento,
        assessments: []
      };
    }
    acc[key].assessments.push(assessment);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center text-xl">
                  <ClipboardList className="h-6 w-6 mr-2" />
                  {language === 'es' ? 'Gestión de Evaluaciones' : 'Assessment Management'}
                </CardTitle>
                <CardDescription>
                  {language === 'es' 
                    ? 'Administra y elimina evaluaciones de empleados y candidatos'
                    : 'Manage and delete employee and candidate assessments'
                  }
                </CardDescription>
              </div>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                {language === 'es' ? 'Volver al Dashboard' : 'Back to Dashboard'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={language === 'es' ? 'Buscar por nombre, email o departamento...' : 'Search by name, email or department...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={language === 'es' ? 'Estado' : 'Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'es' ? 'Todos los estados' : 'All statuses'}</SelectItem>
                  <SelectItem value="completed">{language === 'es' ? 'Completadas' : 'Completed'}</SelectItem>
                  <SelectItem value="in_progress">{language === 'es' ? 'En Progreso' : 'In Progress'}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-full md:w-48">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={language === 'es' ? 'Rol' : 'Role'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'es' ? 'Todos los roles' : 'All roles'}</SelectItem>
                  <SelectItem value="employee">{language === 'es' ? 'Empleados' : 'Employees'}</SelectItem>
                  <SelectItem value="candidate">{language === 'es' ? 'Candidatos' : 'Candidates'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-700">{assessments.length}</div>
                  <div className="text-sm text-blue-600">
                    {language === 'es' ? 'Total Evaluaciones' : 'Total Assessments'}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-700">
                    {assessments.filter(a => a.status === 'completado').length}
                  </div>
                  <div className="text-sm text-green-600">
                    {language === 'es' ? 'Completadas' : 'Completed'}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-yellow-700">
                    {assessments.filter(a => a.status !== 'completado').length}
                  </div>
                  <div className="text-sm text-yellow-600">
                    {language === 'es' ? 'En Progreso' : 'In Progress'}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-purple-700">
                    {Object.keys(groupedByUser).length}
                  </div>
                  <div className="text-sm text-purple-600">
                    {language === 'es' ? 'Usuarios con Evaluaciones' : 'Users with Assessments'}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Assessment List - Grouped by User */}
            <div className="space-y-4">
              {Object.values(groupedByUser).map((userGroup) => (
                <Card key={userGroup.usuario_id} className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div>
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="font-semibold text-lg">{userGroup.usuario_nombre}</span>
                            {getRoleBadge(userGroup.usuario_rol)}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {userGroup.usuario_email}
                            </span>
                            {userGroup.departamento && (
                              <span className="flex items-center">
                                <Building className="h-3 w-3 mr-1" />
                                {userGroup.departamento}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-sm">
                        {userGroup.assessments.length} {language === 'es' ? 'evaluación(es)' : 'assessment(s)'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {userGroup.assessments.map((assessment, index) => (
                        <div 
                          key={assessment.id} 
                          className={`p-4 rounded-lg border ${
                            assessment.status === 'completado' 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-yellow-50 border-yellow-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <span className="font-medium">
                                  #{index + 1} - {assessment.tipo}
                                </span>
                                {getStatusBadge(assessment.status)}
                                <Badge variant="outline">
                                  {assessment.progreso_porcentaje}%
                                </Badge>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                                <div className="flex items-center">
                                  <Calendar className="h-3 w-3 mr-2 text-gray-400" />
                                  <span className="font-medium">
                                    {language === 'es' ? 'Inicio:' : 'Started:'}
                                  </span>
                                  <span className="ml-1">{formatDate(assessment.fecha_inicio)}</span>
                                </div>
                                {assessment.fecha_completado && (
                                  <div className="flex items-center">
                                    <CheckCircle className="h-3 w-3 mr-2 text-green-500" />
                                    <span className="font-medium">
                                      {language === 'es' ? 'Completado:' : 'Completed:'}
                                    </span>
                                    <span className="ml-1">{formatDate(assessment.fecha_completado)}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2 text-xs text-gray-400">
                                ID: {assessment.id}
                              </div>
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteClick(assessment)}
                              className="ml-4"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              {language === 'es' ? 'Eliminar' : 'Delete'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredAssessments.length === 0 && (
                <div className="text-center py-12">
                  <ClipboardList className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {language === 'es' 
                      ? 'No se encontraron evaluaciones con los filtros actuales'
                      : 'No assessments found with current filters'
                    }
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-lg mx-4">
              <CardHeader>
                <CardTitle className="flex items-center text-red-600">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  {language === 'es' ? 'Confirmar Eliminación' : 'Confirm Deletion'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  {language === 'es' 
                    ? '¿Estás seguro de que deseas eliminar esta evaluación? Esta acción no se puede deshacer y el usuario podrá volver a aplicar.'
                    : 'Are you sure you want to delete this assessment? This action cannot be undone and the user will be able to apply again.'
                  }
                </p>
                
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <h4 className="font-semibold mb-2">
                    {language === 'es' ? 'Detalles de la evaluación:' : 'Assessment details:'}
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">{language === 'es' ? 'Usuario:' : 'User:'}</span>
                      <span className="ml-2 font-medium">{deleteConfirm.usuario_nombre}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <span className="ml-2">{deleteConfirm.usuario_email}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{language === 'es' ? 'Tipo:' : 'Type:'}</span>
                      <span className="ml-2">{deleteConfirm.tipo}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{language === 'es' ? 'Estado:' : 'Status:'}</span>
                      <span className="ml-2">{deleteConfirm.status}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">{language === 'es' ? 'Fecha inicio:' : 'Start date:'}</span>
                      <span className="ml-2">{formatDate(deleteConfirm.fecha_inicio)}</span>
                    </div>
                    {deleteConfirm.fecha_completado && (
                      <div className="col-span-2">
                        <span className="text-gray-500">{language === 'es' ? 'Fecha completado:' : 'Completion date:'}</span>
                        <span className="ml-2">{formatDate(deleteConfirm.fecha_completado)}</span>
                      </div>
                    )}
                    <div className="col-span-2">
                      <span className="text-gray-500">ID:</span>
                      <span className="ml-2 font-mono text-xs">{deleteConfirm.id}</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                  >
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={confirmDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    {language === 'es' ? 'Eliminar Evaluación' : 'Delete Assessment'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AssessmentManagement;
